// The playground's PROBLEMS panel: what is wrong with the working copy before the system approves it. Pure; the
// screen adds the editor's own type errors. Sources: the manifest (discovery), the test events and the last results.
import { endpointLabel, endpointOf, routeOfFile } from '@/lib/endpoints'
import { isApiFile } from '@/lib/manifest'
import type { CodeFile, Manifest, TestEvent, TestResult } from '@/types'

export interface Problem {
  level: 'error' | 'warning'
  /** the file it belongs to (clicking opens it) */
  path: string
  line?: number
  text: string
  source: 'discovery' | 'tests' | 'editor' | 'system check' | 'env'
}

/** Discovery's own findings, endpoints without test events, test events whose endpoint is gone, variables without a value. */
export function layoutProblems(files: CodeFile[], manifest: Manifest, tests: TestEvent[], envValues: Record<string, string | null> = {}): Problem[] {
  const out: Problem[] = []
  for (const p of manifest.problems) out.push({ level: 'error', path: p.file, text: p.text, source: 'discovery' })
  for (const e of manifest.endpoints) {
    if (!tests.some((t) => t.endpoint === e.path)) out.push({ level: 'warning', path: e.file, text: `${e.method} ${e.path} has no test events: on the canvas its only port is error`, source: 'tests' })
  }
  // a file under api/ that discovery did not see yet (typed after the last discovery)
  for (const f of files.filter((x) => isApiFile(x.path) && !manifest.endpoints.some((e) => e.file === x.path) && !manifest.problems.some((p) => p.file === x.path))) {
    out.push({ level: routeOfFile(f) ? 'warning' : 'error', path: f.path, text: routeOfFile(f) ? `${routeOfFile(f)} is new: Run or Discover endpoints to add it to the manifest` : 'defines no endpoint: module.exports = defineApi({ outcomes, handler })', source: 'discovery' })
  }
  const orphan = new Map<string, number>()
  for (const t of tests) if (!endpointOf(manifest, t.endpoint)) orphan.set(t.endpoint, (orphan.get(t.endpoint) ?? 0) + 1)
  for (const [route, n] of orphan) out.push({ level: 'error', path: `api${route}.js`, text: `${n} test event${n === 1 ? '' : 's'} point at ${route}, which is not in the code (renamed or deleted?)`, source: 'tests' })
  for (const v of manifest.env) {
    if (!(v.name in envValues) || envValues[v.name] === null || envValues[v.name] === '') out.push({ level: v.name === 'LENDER_TOKEN' ? 'warning' : 'error', path: v.files[0] ?? '', text: `${v.name} has no value for this environment: Env variables tab (tab 5)`, source: 'env' })
  }
  return out
}

/** The last test results: a red test, and every system-check finding on what the endpoint returned. */
export function resultProblems(results: TestResult[], manifest: Manifest): Problem[] {
  const out: Problem[] = []
  for (const r of results) {
    const path = endpointOf(manifest, r.endpoint)?.file ?? `api${r.endpoint}.js`
    const label = endpointLabel(r.endpoint)
    if (!r.ok && !(r.issues ?? []).some((i) => i.level === 'error')) out.push({ level: 'error', path, text: `${label} · “${r.name}”: expected ${r.expectedOutcome}, got ${r.actualOutcome ?? 'nothing'}${r.error ? ` (${r.error})` : ''}`, source: 'tests' })
    for (const i of r.issues ?? []) out.push({ level: i.level, path, text: `${label} · “${r.name}”: ${i.text}`, source: 'system check' })
    for (const v of r.envMissing ?? []) out.push({ level: 'error', path, text: `${label}: the code reads ${v}, which has no value (Env variables tab)`, source: 'env' })
  }
  const seen = new Set<string>()
  return out.filter((p) => {
    const key = `${p.level}|${p.path}|${p.text.replace(/ · “[^”]*”/, '')}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const countOf = (ps: Problem[]) => ({ errors: ps.filter((p) => p.level === 'error').length, warnings: ps.filter((p) => p.level === 'warning').length })
